import { useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { federIndikator, transition } from '../lib/motion';
import { cn } from '../lib/cn';

export interface TabDefinition {
  id: string;
  label: string;
  /** Kürzere Beschriftung für die Leiste auf kleinen Geräten. */
  kurzLabel?: string;
  icon?: ReactNode;
  /** Zähler rechts neben der Beschriftung, z. B. offene Vorgänge. */
  zaehler?: number;
}

export interface TabBarProps {
  tabs: TabDefinition[];
  aktiv: string;
  onWechsel: (id: string) => void;
  ausrichtung?: 'horizontal' | 'vertikal';
  /** Pflicht: benennt die Tab-Gruppe für Screenreader. */
  ariaLabel: string;
  className?: string;
  /** Panel-Id, die von den Tabs gesteuert wird. */
  panelId?: string;
}

/**
 * Tab-Navigation mit gleitendem Aktiv-Indikator.
 *
 * - Der Indikator wandert über `layoutId` mit Feder-Animation zwischen den Tabs.
 * - Tastatur: Pfeiltasten wechseln, Pos1/Ende springen an den Rand (WAI-ARIA Tabs).
 * - Bei `prefers-reduced-motion` springt der Indikator ohne Animation.
 */
export function TabBar({
  tabs,
  aktiv,
  onWechsel,
  ausrichtung = 'horizontal',
  ariaLabel,
  className,
  panelId,
}: TabBarProps) {
  const reduziert = useReducedMotion();
  const indikatorId = useId();
  const knoepfe = useRef<Array<HTMLButtonElement | null>>([]);
  const vertikal = ausrichtung === 'vertikal';

  const aktivIndex = Math.max(
    0,
    tabs.findIndex((t) => t.id === aktiv),
  );

  function waehle(index: number) {
    const ziel = tabs[index];
    if (!ziel) return;
    onWechsel(ziel.id);
    knoepfe.current[index]?.focus();
  }

  function beiTaste(e: KeyboardEvent<HTMLDivElement>) {
    const vor = vertikal ? 'ArrowDown' : 'ArrowRight';
    const zurueck = vertikal ? 'ArrowUp' : 'ArrowLeft';

    if (e.key === vor) {
      e.preventDefault();
      waehle((aktivIndex + 1) % tabs.length);
    } else if (e.key === zurueck) {
      e.preventDefault();
      waehle((aktivIndex - 1 + tabs.length) % tabs.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      waehle(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      waehle(tabs.length - 1);
    }
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation={vertikal ? 'vertical' : 'horizontal'}
      onKeyDown={beiTaste}
      className={cn(
        'flex',
        vertikal ? 'flex-col gap-1' : 'w-full items-stretch gap-1',
        className,
      )}
    >
      {tabs.map((tab, index) => {
        const istAktiv = tab.id === aktiv;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              knoepfe.current[index] = el;
            }}
            role="tab"
            id={`tab-${indikatorId}-${tab.id}`}
            type="button"
            aria-selected={istAktiv}
            aria-controls={panelId}
            tabIndex={istAktiv ? 0 : -1}
            onClick={() => onWechsel(tab.id)}
            className={cn(
              'relative isolate flex min-w-0 select-none items-center rounded-lg',
              'transition-colors duration-fast ease-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
              vertikal
                ? 'w-full gap-3 px-3 py-2.5 text-sm font-medium'
                : 'flex-1 flex-col justify-center gap-1 px-2 py-2 text-2xs font-medium',
              istAktiv ? 'text-accent-soft-fg' : 'text-fg-muted hover:text-fg',
            )}
          >
            {istAktiv && (
              <motion.span
                layoutId={`tabbar-indikator-${indikatorId}`}
                transition={transition(!!reduziert, federIndikator)}
                className="absolute inset-0 -z-10 rounded-lg bg-accent-soft"
                aria-hidden="true"
              />
            )}

            {tab.icon && (
              <span className={cn('shrink-0', istAktiv ? 'text-accent' : 'text-current')}>
                {tab.icon}
              </span>
            )}

            <span className={cn('truncate', vertikal && 'flex-1 text-left')}>
              {vertikal ? tab.label : (tab.kurzLabel ?? tab.label)}
            </span>

            {typeof tab.zaehler === 'number' && tab.zaehler > 0 && (
              <span
                className={cn(
                  'rounded-pill px-1.5 text-2xs font-semibold tabellenziffern',
                  vertikal ? 'bg-surface-3 text-fg-muted' : 'absolute right-2 top-1 bg-accent text-accent-fg',
                )}
              >
                {tab.zaehler}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
